import type { UUID } from '@cobliteam/common-types/src/identifier';
import camelcaseKeys from 'camelcase-keys';
import type { VehicleOdometer } from '../../__generated__/graphql';
import HttpMethod from '../../request/HttpMethod';
import HttpStatusCode from '../../request/HttpStatusCode';
import Requester from '../../request/Requester';
import type { Configuration } from '../../request/Types';
import {
  toMaintenanceRemindersSetupAPI,
  toPastMaintenanceAPI,
  toPastMaintenanceMaps,
  toUpcomingMaintenancesByStatus,
  toVehicleOdometer,
  toVehicleRecurrencesAPI,
} from './APITransformer';
import type {
  MaintenanceOption,
  MaintenanceRemindersSetup,
  PastMaintenance,
  PastMaintenancesMaps,
  Recurrence,
  RegisterPastMaintenance,
  UpcomingMaintenancesByStatus,
} from './Types';

export class MaintenanceRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  // REQUESTS ANTIGOS
  public loadPastMaintenances = (): Promise<PastMaintenancesMaps> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/past',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => toPastMaintenanceMaps(response.data));
  };

  public savePastMaintenance = (pm: RegisterPastMaintenance): Promise<void> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/past',
      method: HttpMethod.POST,
      body: JSON.stringify(toPastMaintenanceAPI(pm)),
    };

    return this.requester.request(requestData).then();
  };

  public markPastMaintenanceAsDone = (pm: RegisterPastMaintenance): Promise<void> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/markasdone',
      method: HttpMethod.POST,
      body: JSON.stringify(toPastMaintenanceAPI(pm)),
    };

    return this.requester.request(requestData).then();
  };

  public deletePastMaintenance = (pm: PastMaintenance): Promise<void> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/past',
      method: HttpMethod.DELETE,
      body: JSON.stringify({
        id: pm.id,
        vehicle_id: pm.vehicleId,
        maintenance_time: pm.date,
      }),
    };

    return this.requester.request(requestData).then();
  };

  public loadMaintenanceItemOptions = (): Promise<MaintenanceOption[]> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/options',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => response.data);
  };

  public loadUpcomingMaintenances = (): Promise<UpcomingMaintenancesByStatus> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/upcoming',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => toUpcomingMaintenancesByStatus(response.data));
  };

  public deleteReminders = (vehicleIds: UUID[]): Promise<void> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/warnings',
      method: HttpMethod.DELETE,
      body: JSON.stringify(vehicleIds),
    };

    return this.requester.request(requestData).then();
  };

  public editReminders = (vehicleId: UUID, recurrences: Recurrence[]): Promise<void> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/warnings',
      method: HttpMethod.PATCH,
      body: JSON.stringify(toVehicleRecurrencesAPI(vehicleId, recurrences)),
    };

    return this.requester.request(requestData).then();
  };

  public setupMaintanenceReminders = (data: MaintenanceRemindersSetup): Promise<void> => {
    const requestData = {
      endpoint: '/maintenance/maintenances/warnings',
      method: HttpMethod.POST,
      body: JSON.stringify(toMaintenanceRemindersSetupAPI(data)),
    };

    return this.requester.request(requestData).then();
  };

  public getVehicleOdometer = async (vehicleId: UUID): Promise<VehicleOdometer> => {
    const requestData = {
      endpoint: `/maintenance/vehicles/${vehicleId}/odometer`,
      method: HttpMethod.GET,
    };

    const odometerApi = await this.requester.request(requestData, [HttpStatusCode.NOT_FOUND]);

    // @ts-ignore
    return camelcaseKeys(odometerApi.data, { deep: true });
  };

  public getFleetOdometers = async (): Promise<VehicleOdometer[]> => {
    const requestData = {
      endpoint: '/maintenance/odometers',
      method: HttpMethod.GET,
    };

    const odometerApi = await this.requester.request(requestData);

    // @ts-ignore
    return camelcaseKeys(odometerApi.data, { deep: true });
  };

  public createVehicleOdometer = async (vehicleId: UUID, currentOdometerInKm: number, odometerDateAt: number): Promise<VehicleOdometer> => {
    const requestData = {
      endpoint: `/maintenance/vehicles/${vehicleId}/odometer?odometer_date_at=${odometerDateAt}`,
      method: HttpMethod.POST,
      body: JSON.stringify({
        current_odometer_in_km: currentOdometerInKm,
      }),
    };

    const odometerApi = await this.requester.request(requestData);

    // @ts-ignore
    return toVehicleOdometer(camelcaseKeys(odometerApi.data, { deep: true }));
  };
}

export default MaintenanceRequester;
